import React, { useEffect, useState } from "react"
import { Box, Button, FormHelperText, Stack, TextField, Typography } from "@mui/material"
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined"
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import { COLORS } from "../../utils"

const sx = {
  // KFC-284: 40px rounded square behind the fridge glyph, per the DSG-17 prototype.
  iconBadge: {
    flex: "0 0 40px",
    width: 40,
    height: 40,
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  card: {
    // padding: "16px 16px 40px 16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingTop: "16px",
    alignItems: "center",
    gap: "40px",
    display: "flex",
    flexDirection: "column",
    borderRadius: "8px",
    border: "1px solid #DBDBDB",
    background: "#FFF",
    alignSelf: "stretch",
  },
  title: {
    color: "#262626",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "28px",
  },
  description: {
    color: "#414141",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
    "& span": {
      fontWeight: 500,
    }
  },
  input: {
    "& .MuiInputBase-input": {
      fontWeight: "normal",
      fontSize: 60,
      lineHeight: 1,
      color: COLORS.BLACK_CARD,
      textAlign: "center",
      width: 126,
      height: 68,
      padding: 0,
    },
    "& ::before": {
      marginTop: 0,
    },
    "&.is-tablet": {
      marginTop: 29,
    },
  },
  errorText: {
    fontSize: "16px",
    color: COLORS.RED_700,
    lineHeight: 1.5,
  },
  // KFC-284: informational pre-orders reminder, deliberately not styled as an error.
  preorders: {
    fontSize: "16px",
    lineHeight: 1.5,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  // The DSG-17 prototype uses the outlined Material "info" glyph in grey. An emoji rendered
  // as a blue filled square instead, which is what the review flagged.
  preordersIcon: {
    fontSize: 20,
    color: "#8A8A8A",
    flex: "0 0 auto",
  },
  validateButton: {
    width: 448,
    height: 72,
    fontSize: 20,
    fontWeight: 500,
    lineHeight: 1.2,
    letterSpacing: "1.25px",
    "&:hover, &:disabled, &:disabled:hover": {
      color: COLORS.WHITE,
      backgroundColor: COLORS.PRIMARY_COLOR,
    },
  },
  confirmationButton: {
    backgroundColor: COLORS.ORANGE_AMBER_900,
    color: COLORS.WHITE,
    "&:hover, &:disabled, &:disabled:hover": {
      color: COLORS.WHITE,
      backgroundColor: COLORS.ORANGE_AMBER_900,
      opacity: 0.8
    },
  }
}

const InputCardNumberWithRetail = ({
  selectedCard,
  isTablet,
  onSubmit,
  onChangeInput,
  initialValues,
  preordersVolume = 0,
  isConfirmation = false
}) => {
  const [values, setValues] = useState({
    foodcheri: "",
    fridgeRetail: ""
  })

  const [errors, setErrors] = useState({
    foodcheri: "",
    fridgeRetail: ""
  })

  useEffect(() => {
    if (!initialValues) return

    setValues(initialValues)
  }, [initialValues])
  const handleChange = (e) => {
    const newValues = {
      ...values,
      [e.target.name]: e.target.value
    }

    setValues(newValues)
    onChangeInput(newValues)

    // KFC-284: the click & collect fridge must be able to honour orders already placed.
    // Wording taken verbatim from the DSG-17 prototype.
    if (e.target.name === "foodcheri" && preordersVolume > 0 && Number(e.target.value) < preordersVolume) {
      setErrors({
        ...errors,
        foodcheri: `Veuillez saisir une quantité d’au moins ${preordersVolume} pour couvrir les précommandes click & collect.`
      })

      return
    }

    setErrors({
      ...errors,
      [e.target.name]: ""
    })
  }

  const isPackaged = selectedCard.packagingNumber > 1 && selectedCard.packagingNumberUnit === "piece"

  const handleSubmit = (e) => {
    e.preventDefault()
    // Treat empty values as 0
    const submittedValues = {
      foodcheri: values.foodcheri === "" ? "0" : values.foodcheri,
      fridgeRetail: values.fridgeRetail === "" ? "0" : values.fridgeRetail
    }
    onSubmit(submittedValues)
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={4} alignItems="center" sx={{ marginTop: "32px" }}>
        {[
          // KFC-284: labels, glyphs and colours taken from the DSG-17 prototype, so that a
          // Comptoir Manager moving between the BO and KFC reads the same words and the same
          // visuals for the same fridge — which is the whole point of the design ticket.
          {
            id: "retail",
            name: "fridgeRetail",
            Icon: StorefrontOutlinedIcon,
            iconColor: "#2E7D45",
            iconBackground: "#EAF6EC",
            title: "Frigo libre-service",
          },
          {
            id: "fc",
            name: "foodcheri",
            Icon: ShoppingBagOutlinedIcon,
            iconColor: "#B45309",
            iconBackground: "#FCF0DF",
            title: "Frigo click & collect",
          },
        ].map((item, index) => (
          <Box
            sx={{
              ...sx.card,
              paddingBottom: errors[item.name] ? "16px" : "40px",
              ...(errors[item.name] ? { borderColor: COLORS.RED_700 } : {})
            }}
            key={item.id + index}
          >
            {/* Top */}
            <Stack direction="row" spacing={2}>
              {/* Decorative: the fridge is named right next to it, so it is hidden from
                  assistive technology rather than announced twice. */}
              <Box sx={{ ...sx.iconBadge, backgroundColor: item.iconBackground }}>
                <item.Icon aria-hidden sx={{ fontSize: 22, color: item.iconColor }} />
              </Box>
              <Stack spacing={1} alignItems="flex-start">
                <Typography variant="h6" sx={sx.title}>
                  {item.title}
                </Typography>
                {isConfirmation
                  ? (
                    <Typography sx={sx.description}>
                      Recomptez la quantité de <span>{selectedCard.uniqueCode}</span> reçue pour ce frigo
                    </Typography>
                  ) : (
                    <Typography sx={sx.description}>
                      Entrez le total de <span>{selectedCard.uniqueCode}</span> conformes reçues pour ce frigo
                    </Typography>
                  )}
              </Stack>
            </Stack>
            {/* Bottom */}
            <div>
              <TextField
                name={item.name}
                variant="standard"
                inputProps={{
                  className: isTablet ? "is-tablet " : "",
                  placeholder: "000",
                  inputMode: "numeric",
                  pattern: "[0-9]*"
                }}
                onChange={handleChange}
                // inputRef={inputRef}
                value={values[item.name]}
                sx={sx.input}
              />
            </div>
            {isPackaged && (
              <div>
                📦 <b>Attendu : {selectedCard.initialVolume / selectedCard.packagingNumber} boîtes</b> (soit {selectedCard.initialVolume} pièces) Merci de saisir le nombre de <b>pièces</b>.
              </div>
            )}
            {/* KFC-284: pre-orders to honour, shown under the click & collect card. */}
            {item.id === "fc" && preordersVolume > 0 && (
              <div>
                <Typography sx={sx.preorders}>
                  <InfoOutlinedIcon aria-hidden sx={sx.preordersIcon} />
                  <span><b>{preordersVolume} précommande{preordersVolume > 1 ? "s" : ""}</b> à placer dans ce frigo.</span>
                </Typography>
              </div>
            )}
            {errors[item.name] && (
              <div>
                <FormHelperText error sx={sx.errorText}>
                  {errors[item.name]}
                </FormHelperText>
              </div>
            )}
          </Box>
        ))}
        {/* button submit */}
        <Button
          className={isTablet ? "is-tablet " : ""}
          sx={{ ...sx.validateButton, ...(isConfirmation ? sx.confirmationButton : {}) }}
          variant="contained"
          color="primary"
          disableElevation
          disabled={errors.fridgeRetail !== "" || errors.foodcheri !== ""}
          type="submit"
        >
          Confirmer
        </Button>
      </Stack>
    </form>
  )
}

export default InputCardNumberWithRetail
